import React, { useCallback, useMemo } from 'react';
import { useNavigation } from '@react-navigation/native';

import { ROLE_PERMISSIONS } from '../../../common/constants';
import { useUserContext } from '../../../common/utils/context';
import {
	GET_DRIVER_MY_DAY_TICKET_ITEMS,
	GET_TICKETS,
	MODIFY_TICKET,
} from '../../../common/graphql/ticket';
import { Screen, SecondaryButton } from '../../design/components';
import PageHeading from '../../design/components/PageHeading';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import useMyDayMode, { MY_DAY_TICKET_SCOPE } from '../myDay/useMyDayMode';
import TicketList from './TicketList';

const TicketListView = () => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const { hasPermission, isDriver, user, isTechnician } = useUserContext();
	const { ticketScope } = useMyDayMode();
	const isDriverMyDay = ticketScope === MY_DAY_TICKET_SCOPE.DRIVER;
	const canAddTickets = Boolean(hasPermission?.(ROLE_PERMISSIONS.ADD_EDIT_TICKETS));

	const openTicketCreator = useCallback(() => {
		navigation.push('TicketCreator');
	}, [navigation]);

	const initialFilter = useMemo(() => {
		if (isDriverMyDay) {
			return undefined;
		}

		if (isDriver) {
			return { driver: user.id };
		}

		if (isTechnician) {
			return { technician: user.id };
		}
	}, [isDriver, isDriverMyDay, isTechnician, user?.id]);

	return (
		<Screen scroll={false}>
			<PageHeading
				title="Tickets"
				style={styles.heading}
				action={canAddTickets && (
					<SecondaryButton label="Create ticket" icon="plus" onPress={openTicketCreator} />
				)}
			/>
			<TicketList
				key={isDriverMyDay ? 'driverMyDayTickets' : 'genericTickets'}
				query={isDriverMyDay ? GET_DRIVER_MY_DAY_TICKET_ITEMS : GET_TICKETS}
				modifyMutation={MODIFY_TICKET}
				initialSortBy="lastRepeated"
				initialSortDirection={-1}
				initialPerPage={10}
				initialFilter={initialFilter}
				showMachineName={true}
				isTicketList={isDriverMyDay}
				emptyListMessage={isDriverMyDay ? 'No tickets for today\'s runs' : undefined}
			/>
		</Screen>
	);
};

const createStyles = ({ ui: { space } }) => ({
	heading: {
		paddingHorizontal: space.gutter,
		paddingTop: 20,
	},
});

export default TicketListView;
